import { useEffect, useMemo, useState } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { usePermissions } from "@/context/PermissionsContext";
import { TID } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  TrendingUp, PlusCircle, RefreshCcw, Search, Loader2, ShieldAlert,
  AlertCircle, Trash2, ArrowRight, Calendar, Users2,
  Sparkles, Copy, CheckCircle2, Mail, DatabaseZap, Trophy,
} from "lucide-react";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip as ChartTooltip, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";

const STAGES = [
  { value: "lead",         label: "Lead",         prob: 5,   colour: "bg-slate-100 text-slate-700 border-slate-200" },
  { value: "qualified",    label: "Qualified",    prob: 20,  colour: "bg-sky-100 text-sky-800 border-sky-200" },
  { value: "quoted",       label: "Quoted",       prob: 45,  colour: "bg-violet-100 text-violet-800 border-violet-200" },
  { value: "negotiation",  label: "Negotiation",  prob: 70,  colour: "bg-amber-100 text-amber-800 border-amber-200" },
  { value: "won",          label: "Won",          prob: 100, colour: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  { value: "lost",         label: "Lost",         prob: 0,   colour: "bg-rose-100 text-rose-800 border-rose-200" },
];
const STAGE_MAP = Object.fromEntries(STAGES.map((s) => [s.value, s]));

const fmtMoney = (n) => n == null ? "—" :
  "$" + Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 });
const fmtDate = (s) => (s ? new Date(s.slice(0, 10)).toLocaleDateString() : "—");
const monthLabel = (yyyymm) => {
  if (yyyymm === "unscheduled") return "No date";
  const [y, m] = (yyyymm || "").split("-").map(Number);
  if (!y) return yyyymm;
  return new Date(y, (m || 1) - 1, 1).toLocaleDateString(undefined, { month: "short", year: "2-digit" });
};

const ym = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
const fyStartYear = (d = new Date()) => (d.getMonth() + 1 >= 7 ? d.getFullYear() : d.getFullYear() - 1);
// Default period: start of LAST financial year → current month ("last FY plus moving forward")
const DEFAULT_FROM = `${fyStartYear() - 1}-07`;
const DEFAULT_TO = ym(new Date());

const SortTh = ({ label, k, sort, setSort, right }) => (
  <th
    className={`${right ? "text-right" : "text-left"} px-3 py-2 font-medium cursor-pointer select-none hover:text-slate-900 whitespace-nowrap`}
    data-testid={`sales-sort-${k}`}
    onClick={() => setSort((s) => ({ key: k, dir: s.key === k ? -s.dir : (right ? -1 : 1) }))}
  >
    {label}{sort.key === k ? (sort.dir === 1 ? " ▲" : " ▼") : ""}
  </th>
);

const StatTile = ({ label, value, sub, testId, tone }) => (
  <div data-testid={testId} className="rounded-lg border border-slate-200 bg-white p-3">
    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">{label}</div>
    <div className={`text-xl font-semibold mt-1 ${tone || "text-slate-800"}`}>{value}</div>
    {sub && <div className="text-[10px] text-slate-400 mt-0.5 truncate" title={sub}>{sub}</div>}
  </div>
);

const sortRows = (rows, { key, dir }) => {
  if (!key) return rows;
  return [...rows].sort((a, b) => {
    const av = a[key], bv = b[key];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === "string" || typeof bv === "string") return dir * String(av).localeCompare(String(bv));
    return dir * ((av > bv) - (av < bv));
  });
};


// ============================================================
// Sales & Estimating page
// ============================================================
export default function SalesPage() {
  const { can, loaded, role } = usePermissions();
  const canView   = can("sales", "view");
  const canCreate = can("sales", "create");
  const canEdit   = can("sales", "edit");
  const canDelete = can("sales", "delete");
  const canSyncAroflo = role === "executive";

  const [summary, setSummary] = useState(null);
  const [forecast, setForecast] = useState([]);
  const [opps, setOpps] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [estimators, setEstimators] = useState([]);
  const [trend, setTrend] = useState([]);
  const [aroflo, setAroflo] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [search, setSearch] = useState("");
  const [stageFilter, setStageFilter] = useState("");
  const [fromMonth, setFromMonth] = useState(DEFAULT_FROM);
  const [toMonth, setToMonth] = useState(DEFAULT_TO);
  const [estSort, setEstSort] = useState({ key: "total_value", dir: -1 });
  const [oppSort, setOppSort] = useState({ key: null, dir: 1 });
  const [quoteSort, setQuoteSort] = useState({ key: null, dir: 1 });

  const [createOpen, setCreateOpen] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [nextActionOpen, setNextActionOpen] = useState(false);
  const [nextActionTarget, setNextActionTarget] = useState(null);

  const load = async () => {
    setRefreshing(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("q", search);
      if (stageFilter) params.set("stage", stageFilter);
      const period = new URLSearchParams();
      if (fromMonth) period.set("date_from", fromMonth);
      if (toMonth) period.set("date_to", toMonth);
      const [{ data: sum }, { data: fc }, { data: opList }, { data: qList }] = await Promise.all([
        api.get(`/sales/pipeline-summary?${period.toString()}`),
        api.get("/sales/forecast?months=6"),
        api.get(`/sales/opportunities?${params.toString()}`),
        api.get("/sales/quotes"),
      ]);
      setSummary(sum);
      setForecast(fc);
      setOpps(opList);
      setQuotes(qList);
      // Secondary analytics — tolerant of individual failures
      const extras = await Promise.allSettled([
        api.get(`/sales/estimator-performance?${period.toString()}`),
        api.get(`/sales/monthly-trend?months=24&${period.toString()}`),
        api.get("/sales/aroflo/status"),
      ]);
      if (extras[0].status === "fulfilled") setEstimators(extras[0].value.data || []);
      if (extras[1].status === "fulfilled") setTrend(extras[1].value.data || []);
      if (extras[2].status === "fulfilled") setAroflo(extras[2].value.data);
    } catch (e) {
      toast.error("Sales failed to load", { description: formatApiErrorDetail(e) });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (!canView) { setLoading(false); return; }
    load();
    // eslint-disable-next-line
  }, [canView, search, stageFilter, fromMonth, toMonth]);

  const maxFcValue = useMemo(
    () => Math.max(1, ...forecast.map((m) => m.value || 0)),
    [forecast]
  );
  const sortedEstimators = useMemo(() => sortRows(estimators, estSort), [estimators, estSort]);
  const sortedOpps = useMemo(
    () => sortRows(opps.map((o) => ({ ...o, _name: o.name || o.title || "" })), oppSort),
    [opps, oppSort]);
  const sortedQuotes = useMemo(() => sortRows(quotes, quoteSort), [quotes, quoteSort]);

  if (!loaded) return <AppLayout><div className="p-8">Loading…</div></AppLayout>;
  if (!canView) {
    return (
      <AppLayout>
        <div className="max-w-md mx-auto mt-24 rounded-lg border border-slate-200 bg-white p-6 text-center">
          <ShieldAlert className="w-8 h-8 mx-auto text-rose-500 mb-2" />
          <div className="font-semibold text-slate-800 mb-1">Restricted</div>
          <p className="text-sm text-slate-600">Your role does not have access to the Sales pipeline.</p>
        </div>
      </AppLayout>
    );
  }

  const funnelStages = summary?.by_stage?.filter((s) =>
    ["lead", "qualified", "quoted", "negotiation", "won", "lost"].includes(s.stage)
  ) || [];
  const maxFunnel = Math.max(1, ...funnelStages.map((s) => s.count || 0));

  const changeStage = async (opp, newStage) => {
    try {
      const { data } = await api.patch(`/sales/opportunities/${opp.id}`, {
        stage: newStage, last_activity_at: new Date().toISOString(),
      });
      setOpps((rows) => rows.map((r) => (r.id === opp.id ? data : r)));
      toast.success(`Moved to ${STAGE_MAP[newStage]?.label || newStage}`);
      // Reload summaries when a stage changes so KPIs update.
      load();
    } catch (e) {
      toast.error("Update failed", { description: formatApiErrorDetail(e) });
    }
  };

  return (
    <AppLayout>
      <div data-testid="sales-page" className="p-6 max-w-[1400px] mx-auto space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-display text-slate-900 flex items-center gap-2">
              <TrendingUp className="w-7 h-7 text-gbe" />
              Sales &amp; Estimating
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              Pipeline · Weighted forecast · Quotes register · Win/loss. Section 11 of the scope.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={load} disabled={refreshing}>
              {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
              <span className="ml-2">Refresh</span>
            </Button>
            {canCreate && (
              <>
                <Button variant="outline" size="sm" onClick={() => setQuoteOpen(true)}>
                  <PlusCircle className="w-4 h-4 mr-2" />New quote
                </Button>
                <Button size="sm" className="bg-gbe hover:bg-gbe/90 text-white"
                        data-testid={TID.salesCreateOpen}
                        onClick={() => setCreateOpen(true)}>
                  <PlusCircle className="w-4 h-4 mr-2" />New opportunity
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Period selector — drives KPI tiles, funnel, chart + estimator table */}
        <div className="rounded-lg border border-slate-200 bg-white px-4 py-2.5 flex flex-wrap items-center gap-3" data-testid="sales-period-bar">
          <Calendar className="w-4 h-4 text-gbe" />
          <span className="text-sm font-medium text-slate-700">Period</span>
          <input type="month" value={fromMonth} max={toMonth || undefined}
                 onChange={(e) => setFromMonth(e.target.value)}
                 data-testid="sales-period-from"
                 className="h-8 rounded-md border border-slate-200 px-2 text-xs text-slate-700 bg-white" />
          <span className="text-xs text-slate-400">→</span>
          <input type="month" value={toMonth} min={fromMonth || undefined}
                 onChange={(e) => setToMonth(e.target.value)}
                 data-testid="sales-period-to"
                 className="h-8 rounded-md border border-slate-200 px-2 text-xs text-slate-700 bg-white" />
          <div className="flex items-center gap-1.5 ml-auto">
            {[
              { label: "This FY", from: `${fyStartYear()}-07`, to: ym(new Date()) },
              { label: "Last FY → now", from: DEFAULT_FROM, to: ym(new Date()) },
              { label: "Last 90 days", from: ym(new Date(Date.now() - 90 * 86400000)), to: ym(new Date()) },
              { label: "All time", from: "", to: "" },
            ].map((p) => (
              <button key={p.label}
                      data-testid={`sales-period-preset-${p.label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                      onClick={() => { setFromMonth(p.from); setToMonth(p.to); }}
                      className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${fromMonth === p.from && toMonth === p.to ? "bg-gbe text-white border-gbe" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                {p.label}
              </button>
            ))}
          </div>
          <div className="w-full text-[11px] text-slate-400 -mt-1">
            Applies to the KPI tiles, pipeline funnel, monthly chart and estimator table · based on quote created date · Won = AroFlo "Approved" (quotes still "Awaiting Decision" stay open)
          </div>
        </div>

        {/* KPI row — period-aware */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatTile testId={TID.salesStatCard("pipeline")} label="Open pipeline"
                    value={fmtMoney(summary?.total_pipeline_value)}
                    sub={`open quotes · ${monthLabel(fromMonth) || "all time"} → ${monthLabel(toMonth) || "now"}`} />
          <StatTile testId={TID.salesStatCard("weighted")} label="Weighted pipeline"
                    value={fmtMoney(summary?.weighted_pipeline_value)}
                    sub="open value × stage probability" />
          <StatTile testId={TID.salesStatCard("won")} label="Won value" tone="text-emerald-700"
                    value={fmtMoney(summary?.won_value)}
                    sub={`${summary?.won_count ?? 0} won · ${summary?.lost_count ?? 0} lost`} />
          <StatTile testId={TID.salesStatCard("winrate")} label="Win rate"
                    tone={summary?.win_rate_pct >= 50 ? "text-emerald-700" : "text-slate-800"}
                    value={summary?.win_rate_pct == null ? "—" : `${summary.win_rate_pct}%`}
                    sub="won ÷ (won + lost) · excl $0 quotes" />
          <StatTile testId={TID.salesStatCard("conversion")} label="Conversion"
                    value={summary?.conversion_pct == null ? "—" : `${summary.conversion_pct}%`}
                    sub="won ÷ all quotes · excl $0 quotes" />
          <StatTile testId={TID.salesStatCard("avgdeal")} label="Avg deal size"
                    value={fmtMoney(summary?.average_deal_size)}
                    sub="average won quote value" />
        </div>

        {/* AroFlo quotes feed */}
        <div data-testid="sales-aroflo-bar" className="rounded-lg border border-slate-200 bg-white px-4 py-2.5 flex flex-wrap items-center gap-3">
          <DatabaseZap className="w-4 h-4 text-gbe" />
          <div className="text-sm text-slate-700">
            <span className="font-medium">AroFlo quotes feed</span>
            <span className="text-slate-500 ml-2 text-xs">
              {aroflo ? (
                <>
                  {(aroflo.aroflo_opportunities ?? 0).toLocaleString()} quotes synced
                  {aroflo.earliest_quote && aroflo.latest_quote && (
                    <span className="text-amber-700 font-medium"> · quote history covers {monthLabel(aroflo.earliest_quote.slice(0, 7))} → {monthLabel(aroflo.latest_quote.slice(0, 7))} only — periods wider than this show identical figures</span>
                  )}
                  {aroflo.last_run?.finished_at && <> · last run {new Date(aroflo.last_run.finished_at).toLocaleString()}</>}
                  {aroflo.last_run && !aroflo.last_run.ok && <span className="text-rose-600"> · last run failed</span>}
                </>
              ) : "status unavailable"}
            </span>
          </div>
          {canSyncAroflo && (
            <Button size="sm" variant="outline" className="ml-auto" disabled={syncing}
                    data-testid="sales-aroflo-sync-btn"
                    onClick={async () => {
                      setSyncing(true);
                      try {
                        const { data } = await api.post("/sales/aroflo/sync");
                        if (data.already_running) toast.info("A sync is already running — hang tight");
                        else toast.success("Sync started", { description: "Pulling the full quote history in the background (~1–2 min)." });
                        for (let i = 0; i < 48; i++) {
                          await new Promise((r) => setTimeout(r, 5000));
                          try {
                            const { data: st } = await api.get("/sales/aroflo/status");
                            setAroflo(st);
                            if (!st.running) {
                              if (st.last_run?.ok) toast.success(`AroFlo sync complete — ${(st.last_run.rows ?? 0).toLocaleString()} quotes (${st.last_run.inserts ?? 0} new)`);
                              else toast.error("AroFlo sync failed", { description: st.last_run?.error || "See logs" });
                              break;
                            }
                          } catch { /* transient poll error — keep waiting */ }
                        }
                        load();
                      } catch (e) {
                        toast.error("Sync failed", { description: formatApiErrorDetail(e) });
                      } finally { setSyncing(false); }
                    }}>
              {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <RefreshCcw className="w-3.5 h-3.5 mr-1.5" />}
              {syncing ? "Syncing…" : "Sync AroFlo quotes"}
            </Button>
          )}
        </div>

        {/* Funnel + Forecast */}
        <div className="grid md:grid-cols-2 gap-4">
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <div className="font-semibold text-slate-800 mb-3">Pipeline funnel</div>
            {funnelStages.length === 0 ? (
              <div className="text-sm text-slate-500 italic py-6 text-center">
                No opportunities recorded yet.
              </div>
            ) : (
              <div className="space-y-2">
                {funnelStages.map((s) => {
                  const meta = STAGE_MAP[s.stage] || {};
                  const pct = Math.round(((s.count || 0) / maxFunnel) * 100);
                  return (
                    <div key={s.stage} className="flex items-center gap-3">
                      <div className="w-24 text-xs text-slate-600">{meta.label || s.stage}</div>
                      <div className="flex-1 h-6 bg-slate-100 rounded overflow-hidden">
                        <div
                          className={`h-full ${meta.colour?.replace("text-", "bg-").split(" ")[0] || "bg-slate-400"}`}
                          style={{ width: `${Math.max(5, pct)}%` }}
                        />
                      </div>
                      <div className="w-16 text-right text-sm font-medium text-slate-800">{s.count}</div>
                      <div className="w-28 text-right text-xs text-slate-500">{fmtMoney(s.value)}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <div className="font-semibold text-slate-800 mb-3">Weighted forecast (next 6 months)</div>
            {forecast.length === 0 ? (
              <div className="text-sm text-slate-500 italic py-6 text-center">No forecast yet.</div>
            ) : (
              <div className="grid grid-cols-6 gap-2">
                {forecast.slice(0, 6).map((m) => {
                  const pct = Math.max(6, Math.round(((m.value || 0) / maxFcValue) * 100));
                  return (
                    <div key={m.month} className="flex flex-col items-center text-center">
                      <div className="w-full h-24 flex items-end">
                        <div className="w-full rounded-t bg-gradient-to-t from-emerald-400 to-emerald-200"
                             style={{ height: `${pct}%` }}
                             title={`${m.count} opps · ${fmtMoney(m.value)} raw · ${fmtMoney(m.weighted)} weighted`} />
                      </div>
                      <div className="text-[10px] text-slate-500 mt-1">{monthLabel(m.month)}</div>
                      <div className="text-[11px] font-semibold text-slate-700">{fmtMoney(m.weighted)}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Monthly trend + Estimator performance */}
        <div className="grid lg:grid-cols-5 gap-4">
          <div className="rounded-lg border border-slate-200 bg-white p-4 lg:col-span-2" data-testid="sales-monthly-trend">
            <div className="font-semibold text-slate-800 mb-3">Quoted vs won value by month</div>
            {trend.length === 0 ? (
              <div className="text-sm text-slate-500 italic py-6 text-center">No dated quotes yet.</div>
            ) : (
              <div className="h-56 min-h-[224px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={trend}>
                    <CartesianGrid strokeDasharray="2 2" stroke="#e2e8f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 10 }} tickFormatter={monthLabel} />
                    <YAxis tick={{ fontSize: 10 }} width={54}
                           tickFormatter={(v) => "$" + (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
                    <ChartTooltip formatter={(v, name) => (name === "Quoted" || name === "Won") ? fmtMoney(v) : v} />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                    <Bar dataKey="value"     name="Quoted" fill="#0f766e" />
                    <Bar dataKey="won_value" name="Won"    fill="#65a30d" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden lg:col-span-3" data-testid="sales-estimator-table">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
              <Trophy className="w-4 h-4 text-amber-500" />
              <span className="font-semibold text-slate-800">Estimator performance</span>
              <span className="text-[11px] text-slate-400 ml-auto">{monthLabel(fromMonth) || "all time"} → {monthLabel(toMonth) || "now"}</span>
            </div>
            {estimators.length === 0 ? (
              <div className="p-6 text-sm text-slate-500 text-center">No quotes in this period — widen the period or sync AroFlo quotes.</div>
            ) : (
              <div className="overflow-x-auto max-h-[360px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-slate-50 z-10">
                    <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                      <SortTh label="Estimator" k="estimator" sort={estSort} setSort={setEstSort} />
                      <SortTh label="Quotes" k="quotes" sort={estSort} setSort={setEstSort} right />
                      <SortTh label="$0 quotes" k="zero_value" sort={estSort} setSort={setEstSort} right />
                      <SortTh label="Quoted value" k="total_value" sort={estSort} setSort={setEstSort} right />
                      <SortTh label="Won" k="won" sort={estSort} setSort={setEstSort} right />
                      <SortTh label="Won value" k="won_value" sort={estSort} setSort={setEstSort} right />
                      <SortTh label="Lost" k="lost" sort={estSort} setSort={setEstSort} right />
                      <SortTh label="Open value" k="open_value" sort={estSort} setSort={setEstSort} right />
                      <SortTh label="Win rate" k="win_rate_pct" sort={estSort} setSort={setEstSort} right />
                      <SortTh label="Conversion" k="conversion_pct" sort={estSort} setSort={setEstSort} right />
                    </tr>
                  </thead>
                  <tbody>
                    {sortedEstimators.map((r) => (
                      <tr key={r.estimator} data-testid={`sales-estimator-row-${r.estimator}`} className="border-b border-slate-100 hover:bg-slate-50/60">
                        <td className="px-3 py-2 font-medium text-slate-800 whitespace-nowrap">{r.estimator}</td>
                        <td className="px-3 py-2 text-right">{r.quotes}</td>
                        <td className="px-3 py-2 text-right text-slate-400">{r.zero_value}</td>
                        <td className="px-3 py-2 text-right">{fmtMoney(r.total_value)}</td>
                        <td className="px-3 py-2 text-right text-emerald-700">{r.won}</td>
                        <td className="px-3 py-2 text-right text-emerald-700">{fmtMoney(r.won_value)}</td>
                        <td className="px-3 py-2 text-right text-rose-600">{r.lost}</td>
                        <td className="px-3 py-2 text-right">{fmtMoney(r.open_value)}</td>
                        <td className="px-3 py-2 text-right font-medium">
                          {r.win_rate_pct == null ? "—" : (
                            <span className={r.win_rate_pct >= 50 ? "text-emerald-700" : r.win_rate_pct >= 25 ? "text-amber-700" : "text-rose-600"}>
                              {r.win_rate_pct}%
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right">{r.conversion_pct == null ? "—" : `${r.conversion_pct}%`}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="px-4 py-2 border-t border-slate-100 text-[11px] text-slate-400">
              Win rate = won ÷ (won + lost) · Conversion = won ÷ all quotes — both exclude $0-value quotes (specs). Click any column to sort.
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="rounded-lg border border-slate-200 bg-white p-3 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)}
                   placeholder="Search opportunities…" className="pl-8"
                   data-testid="sales-search-input" />
          </div>
          <Select value={stageFilter || "__all"} onValueChange={(v) => setStageFilter(v === "__all" ? "" : v)}>
            <SelectTrigger className="w-[180px]"><SelectValue placeholder="Stage" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">All stages</SelectItem>
              {STAGES.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Opportunities table */}
        <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
          <div className="px-3 py-2 bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
            Opportunities
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <SortTh label="Opportunity" k="_name" sort={oppSort} setSort={setOppSort} />
                <SortTh label="Client" k="client_name" sort={oppSort} setSort={setOppSort} />
                <SortTh label="Stage" k="stage" sort={oppSort} setSort={setOppSort} />
                <SortTh label="Value" k="value" sort={oppSort} setSort={setOppSort} right />
                <SortTh label="Expected close" k="expected_close_date" sort={oppSort} setSort={setOppSort} />
                <SortTh label="Owner" k="owner" sort={oppSort} setSort={setOppSort} />
                <th className="text-right px-3 py-2 font-medium">&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="p-6 text-center text-slate-400">Loading…</td></tr>
              ) : opps.length === 0 ? (
                <tr><td colSpan={7} className="p-8 text-center text-slate-500">
                  <AlertCircle className="w-6 h-6 mx-auto mb-2 opacity-50" />
                  No opportunities yet. Click <b>New opportunity</b> to add one.
                </td></tr>
              ) : sortedOpps.map((o) => {
                const meta = STAGE_MAP[o.stage] || {};
                return (
                  <tr key={o.id} data-testid={TID.salesRow(o.id)} className="border-t hover:bg-slate-50/60">
                    <td className="px-3 py-2 font-medium text-slate-800">
                      {o.name || o.title || "—"}
                      {o.source === "aroflo" && <Badge variant="outline" className="ml-2 text-[9px] px-1 py-0 text-slate-500 border-slate-200">AroFlo</Badge>}
                    </td>
                    <td className="px-3 py-2 text-slate-700">{o.client_name || "—"}</td>
                    <td className="px-3 py-2">
                      {canEdit ? (
                        <Select value={o.stage} onValueChange={(v) => changeStage(o, v)}>
                          <SelectTrigger className="h-7 w-[130px] text-xs" data-testid={TID.salesStageSelect(o.id)}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {STAGES.map((s) => (
                              <SelectItem key={s.value} value={s.value}>{s.label} ({s.prob}%)</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${meta.colour}`}>
                          {meta.label || o.stage}
                        </Badge>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right text-slate-700">{fmtMoney(o.value)}</td>
                    <td className="px-3 py-2 text-slate-700">{fmtDate(o.expected_close_date)}</td>
                    <td className="px-3 py-2 text-slate-700 text-xs">{o.owner || "—"}</td>
                    <td className="px-3 py-2 text-right space-x-1">
                      {canEdit && (
                        <Button size="sm" variant="ghost"
                                title="AI next action"
                                data-testid={TID.salesNextActionOpen(o.id)}
                                onClick={() => { setNextActionTarget(o); setNextActionOpen(true); }}>
                          <Sparkles className="w-3.5 h-3.5 text-violet-600" />
                        </Button>
                      )}
                      {canDelete && (
                        <Button size="sm" variant="ghost"
                                onClick={async () => {
                                  if (!window.confirm(`Delete opportunity "${o.name}"?`)) return;
                                  try {
                                    await api.delete(`/sales/opportunities/${o.id}`);
                                    toast.success("Deleted");
                                    load();
                                  } catch (e) {
                                    toast.error("Delete failed", { description: formatApiErrorDetail(e) });
                                  }
                                }}>
                          <Trash2 className="w-3.5 h-3.5 text-rose-500" />
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Quotes table */}
        <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
          <div className="px-3 py-2 bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
            Quotes ({quotes.length})
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <SortTh label="Ref" k="reference" sort={quoteSort} setSort={setQuoteSort} />
                <SortTh label="Client" k="client_name" sort={quoteSort} setSort={setQuoteSort} />
                <SortTh label="Status" k="status" sort={quoteSort} setSort={setQuoteSort} />
                <SortTh label="Value" k="value" sort={quoteSort} setSort={setQuoteSort} right />
                <SortTh label="Issued" k="issued_date" sort={quoteSort} setSort={setQuoteSort} />
                <SortTh label="Valid until" k="valid_until" sort={quoteSort} setSort={setQuoteSort} />
              </tr>
            </thead>
            <tbody>
              {quotes.length === 0 ? (
                <tr><td colSpan={6} className="p-6 text-center text-slate-500">No quotes recorded.</td></tr>
              ) : sortedQuotes.map((q) => (
                <tr key={q.id} className="border-t">
                  <td className="px-3 py-2 font-medium text-slate-800">{q.reference}</td>
                  <td className="px-3 py-2 text-slate-700">{q.client_name || "—"}</td>
                  <td className="px-3 py-2 text-slate-600 capitalize">{q.status?.replace("_", " ")}</td>
                  <td className="px-3 py-2 text-right text-slate-700">{fmtMoney(q.value)}</td>
                  <td className="px-3 py-2 text-slate-700">{fmtDate(q.issued_date)}</td>
                  <td className="px-3 py-2 text-slate-700">{fmtDate(q.valid_until)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <CreateOppDialog open={createOpen} onOpenChange={setCreateOpen} onSaved={load} />
      <CreateQuoteDialog open={quoteOpen} onOpenChange={setQuoteOpen} onSaved={load} opps={opps} />
      <NextActionDialog open={nextActionOpen} onOpenChange={setNextActionOpen}
                        opp={nextActionTarget} />
    </AppLayout>
  );
}


// ============================================================
// Create-opportunity dialog
// ============================================================
function CreateOppDialog({ open, onOpenChange, onSaved }) {
  const [f, setF] = useState({
    name: "", client_name: "", value: "", stage: "lead",
    expected_close_date: "", owner: "", estimator: "", notes: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target ? e.target.value : e }));

  const submit = async () => {
    if (!f.name.trim()) return toast.error("Opportunity name required");
    setSaving(true);
    try {
      await api.post("/sales/opportunities", {
        ...f, value: f.value ? Number(f.value) : null,
      });
      toast.success("Opportunity created");
      onOpenChange(false);
      setF({ name: "", client_name: "", value: "", stage: "lead",
             expected_close_date: "", owner: "", estimator: "", notes: "" });
      onSaved?.();
    } catch (e) {
      toast.error("Create failed", { description: formatApiErrorDetail(e) });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>New opportunity</DialogTitle>
          <DialogDescription>Add an opportunity to the sales pipeline.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="text-xs text-slate-500">Opportunity name *</label>
            <Input value={f.name} onChange={set("name")} placeholder="e.g. UoN Chiller Replacement" />
          </div>
          <div>
            <label className="text-xs text-slate-500">Client name</label>
            <Input value={f.client_name} onChange={set("client_name")} />
          </div>
          <div>
            <label className="text-xs text-slate-500">Stage</label>
            <Select value={f.stage} onValueChange={(v) => setF((s) => ({ ...s, stage: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {STAGES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-500">Value ($)</label>
            <Input type="number" value={f.value} onChange={set("value")} placeholder="250000" />
          </div>
          <div>
            <label className="text-xs text-slate-500">Expected close</label>
            <Input type="date" value={f.expected_close_date} onChange={set("expected_close_date")} />
          </div>
          <div>
            <label className="text-xs text-slate-500">Owner</label>
            <Input value={f.owner} onChange={set("owner")} placeholder="owner@gbegroup.com" />
          </div>
          <div>
            <label className="text-xs text-slate-500">Estimator</label>
            <Input value={f.estimator} onChange={set("estimator")} placeholder="estimator name" />
          </div>
          <div className="col-span-2">
            <label className="text-xs text-slate-500">Notes</label>
            <Textarea rows={2} value={f.notes} onChange={set("notes")} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving}
                  data-testid={TID.salesCreateSubmit}
                  className="bg-gbe hover:bg-gbe/90 text-white">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ============================================================
// Create-quote dialog
// ============================================================
function CreateQuoteDialog({ open, onOpenChange, onSaved, opps }) {
  const [f, setF] = useState({
    reference: "", client_name: "", value: "", status: "draft",
    issued_date: "", valid_until: "", opportunity_id: "", reviewer: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target ? e.target.value : e }));

  const submit = async () => {
    if (!f.reference.trim()) return toast.error("Quote reference required");
    setSaving(true);
    try {
      await api.post("/sales/quotes", {
        ...f,
        value: f.value ? Number(f.value) : null,
        opportunity_id: f.opportunity_id || null,
      });
      toast.success("Quote created");
      onOpenChange(false);
      setF({ reference: "", client_name: "", value: "", status: "draft",
             issued_date: "", valid_until: "", opportunity_id: "", reviewer: "" });
      onSaved?.();
    } catch (e) {
      toast.error("Create failed", { description: formatApiErrorDetail(e) });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>New quote</DialogTitle>
          <DialogDescription>Record a quote against an opportunity or standalone.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="text-xs text-slate-500">Reference *</label>
            <Input value={f.reference} onChange={set("reference")} placeholder="Q-2026-0001" />
          </div>
          <div className="col-span-2">
            <label className="text-xs text-slate-500">Opportunity (optional)</label>
            <Select value={f.opportunity_id || "__none"}
                    onValueChange={(v) => setF((s) => ({ ...s, opportunity_id: v === "__none" ? "" : v }))}>
              <SelectTrigger><SelectValue placeholder="No linked opportunity" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">— No opportunity —</SelectItem>
                {opps.map((o) => (
                  <SelectItem key={o.id} value={o.id}>{o.name} · {fmtMoney(o.value)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-500">Client</label>
            <Input value={f.client_name} onChange={set("client_name")} />
          </div>
          <div>
            <label className="text-xs text-slate-500">Value ($)</label>
            <Input type="number" value={f.value} onChange={set("value")} />
          </div>
          <div>
            <label className="text-xs text-slate-500">Status</label>
            <Select value={f.status} onValueChange={(v) => setF((s) => ({ ...s, status: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {["draft", "issued", "under_review", "accepted", "declined", "expired"].map((s) => (
                  <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-500">Reviewer</label>
            <Input value={f.reviewer} onChange={set("reviewer")} />
          </div>
          <div>
            <label className="text-xs text-slate-500">Issued</label>
            <Input type="date" value={f.issued_date} onChange={set("issued_date")} />
          </div>
          <div>
            <label className="text-xs text-slate-500">Valid until</label>
            <Input type="date" value={f.valid_until} onChange={set("valid_until")} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-gbe hover:bg-gbe/90 text-white">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save quote"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ============================================================
// AI: Next-action dialog (Chase this lead)
// ============================================================
function NextActionDialog({ open, onOpenChange, opp }) {
  const [style, setStyle] = useState("email");
  const [additional, setAdditional] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [sendTo, setSendTo] = useState("");
  const [subject, setSubject] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) {
      setResult(null); setLoading(false); setAdditional(""); setCopied(false);
      setSendTo(""); setSubject(""); setSending(false);
    } else if (opp) {
      const own = opp.owner || "";
      if (/@/.test(own)) setSendTo(own);
      setSubject(`Following up — ${opp.name}`);
    }
  }, [open, opp]);

  if (!opp) return null;

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/sales/opportunities/${opp.id}/next-action`, {
        style, additional_context: additional || null,
      });
      setResult(data);
    } catch (e) {
      toast.error("AI request failed", { description: formatApiErrorDetail(e) });
    } finally { setLoading(false); }
  };

  const copy = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.draft_message || "");
      setCopied(true); toast.success("Copied");
      setTimeout(() => setCopied(false), 2500);
    } catch { toast.error("Copy failed"); }
  };

  const priorityColour = { high: "text-rose-700", medium: "text-amber-700", low: "text-slate-600" };

  const sendViaResend = async () => {
    if (!result?.draft_message) return;
    if (!/@/.test(sendTo || "")) {
      toast.error("Enter a valid recipient email");
      return;
    }
    if (!subject.trim()) {
      toast.error("Enter a subject");
      return;
    }
    setSending(true);
    try {
      await api.post(`/sales/opportunities/${opp.id}/send-message`, {
        to_email: sendTo,
        subject,
        body: result.draft_message,
        opportunity_id: opp.id,
        kind: "sales_next_action",
      });
      toast.success(`Message sent to ${sendTo}`);
      onOpenChange(false);
    } catch (e) {
      toast.error("Send failed", { description: formatApiErrorDetail(e) });
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-violet-600" />
            AI next action
          </DialogTitle>
          <DialogDescription>
            {opp.name} — {opp.client_name || "no client"} — {STAGE_MAP[opp.stage]?.label || opp.stage} ·{" "}
            {fmtMoney(opp.value)}
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="space-y-3">
            <div>
              <label className="text-xs text-slate-500">Output style</label>
              <Select value={style} onValueChange={setStyle}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="email">Chase email</SelectItem>
                  <SelectItem value="phone_talking_points">Phone talking points</SelectItem>
                  <SelectItem value="short_summary">Short summary</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-500">Additional context (optional)</label>
              <Textarea rows={3} value={additional} onChange={(e) => setAdditional(e.target.value)}
                        placeholder="Anything I should know? e.g. 'client asked for a discount', 'we've been chasing for 3 weeks'…" />
            </div>
          </div>
        ) : (
          <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
            <div className="flex items-center gap-3">
              <div className={`text-sm font-medium ${priorityColour[result.priority]}`}>
                Priority: {result.priority}
              </div>
              {result.risk_flags?.length ? (
                <div className="text-xs text-rose-700">
                  Flags: {result.risk_flags.join(" · ")}
                </div>
              ) : null}
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">
                Recommended actions
              </div>
              <ul className="list-disc list-inside text-sm text-slate-700 space-y-0.5">
                {result.recommended_actions.map((a, i) => <li key={i}>{a}</li>)}
              </ul>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Draft message</div>
              <Textarea rows={9} value={result.draft_message || ""}
                        onChange={(e) => setResult({ ...result, draft_message: e.target.value })}
                        className="font-mono text-xs" />
            </div>
            <div className="text-[11px] text-slate-400">
              Model: {result.model} · Confidence {result.confidence}%
            </div>
          </div>
        )}

        <DialogFooter>
          {!result ? (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>Cancel</Button>
              <Button onClick={generate} disabled={loading}
                      className="bg-violet-600 hover:bg-violet-700 text-white">
                {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Working…</> :
                          <><Sparkles className="w-4 h-4 mr-2" />Generate</>}
              </Button>
            </>
          ) : (
            <div className="flex flex-col gap-2 w-full">
              {style === "email" ? (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    <Input type="email" value={sendTo}
                           onChange={(e) => setSendTo(e.target.value)}
                           placeholder="recipient@example.com"
                           data-testid="sales-next-action-recipient" />
                    <Input value={subject}
                           onChange={(e) => setSubject(e.target.value)}
                           placeholder="Subject" />
                  </div>
                  <div className="flex items-center gap-2 justify-end">
                    <Button variant="ghost" onClick={() => setResult(null)}>Regenerate</Button>
                    <Button variant="outline" onClick={copy}
                            data-testid={TID.salesNextActionCopy}>
                      {copied ? <><CheckCircle2 className="w-4 h-4 mr-2 text-emerald-600" />Copied</> :
                                <><Copy className="w-4 h-4 mr-2" />Copy</>}
                    </Button>
                    <Button onClick={sendViaResend} disabled={sending}
                            data-testid="sales-next-action-send"
                            className="bg-gbe hover:bg-gbe/90 text-white">
                      {sending ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Sending…</> :
                                 <><Mail className="w-4 h-4 mr-2" />Send</>}
                    </Button>
                  </div>
                </>
              ) : (
                <div className="flex items-center gap-2 justify-end">
                  <Button variant="ghost" onClick={() => setResult(null)}>Regenerate</Button>
                  <Button variant="outline" onClick={copy}
                          data-testid={TID.salesNextActionCopy}>
                    {copied ? <><CheckCircle2 className="w-4 h-4 mr-2 text-emerald-600" />Copied</> :
                              <><Copy className="w-4 h-4 mr-2" />Copy</>}
                  </Button>
                </div>
              )}
              <div className="text-[11px] text-slate-400">
                Send delivers via Resend and logs to Audit Trail + Email Log.
              </div>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

